/* -----------------Components--------------- */
import { Button, type ButtonProps } from "@/components/ui/button";
import Icon, { type IconName } from "./Icon";

/* -----------------Helpers--------------- */
import { cn } from "@/lib/utils";

/* -----------------Features--------------- */
import { ElementAnalyticsWrapper, trackingEvents } from "@/analytics";

/* -----------------Globals--------------- */
import Link from "next/link";

export type ActionButtonProps = {
	name: string;
	href: string;
	external?: boolean;
	variant?: ButtonProps["variant"];
	size?: ButtonProps["size"];
	iconLeft?: IconName | null;
	iconRight?: IconName | null;
	className?: string;
	disabled?: boolean;
	respondToParentHover?: boolean;
};

export const ActionButton = ({
	name,
	href,
	external,
	variant,
	size,
	iconLeft,
	iconRight,
	className,
	disabled,
	respondToParentHover,
}: ActionButtonProps) => {
	href = href || "#";
	const isLinkVariant = variant === "link";

	return (
		<ElementAnalyticsWrapper
			eventName={trackingEvents.BUTTON_CLICK}
			eventProperties={{
				name,
				redirectTo: href || "",
				externalRedirect: external ? "true" : "false",
			}}
		>
			<Link
				href={href}
				target={external ? "_blank" : "_self"}
				rel={external ? "noopener noreferrer" : ""}
				className="cursor-pointer"
			>
				<Button
					size={size}
					variant={variant}
					className={cn(
						"group relative flex cursor-pointer items-center gap-2 overflow-hidden transition-transform duration-300",
						{
							"hover:scale-105": !(isLinkVariant || respondToParentHover),
							"group-hover/parent:scale-105":
								!isLinkVariant && respondToParentHover,
							"bg-zinc-800 text-white hover:bg-zinc-700":
								variant === "secondary",
						},

						className,
					)}
					disabled={disabled}
				>
					<div className="relative h-6 overflow-hidden">
						<div
							className={cn(
								"flex flex-col transition-transform duration-300",
								respondToParentHover
									? "group-hover/parent:-translate-y-1/2"
									: "group-hover:-translate-y-1/2",
							)}
						>
							<span className="flex h-6 items-center gap-2">
								{!!iconLeft && <Icon name={iconLeft} size={16} />}
								{name}
								{!!iconRight && <Icon name={iconRight} size={16} />}
							</span>
							<span className="flex h-6 items-center gap-2">
								{!!iconLeft && <Icon name={iconLeft} size={16} />}
								{name}
								{!!iconRight && <Icon name={iconRight} size={16} />}
							</span>
						</div>
					</div>
				</Button>
			</Link>
		</ElementAnalyticsWrapper>
	);
};
